import { forwardRef } from 'react';
import type { ComponentProps, FunctionComponent, ChangeEvent, FocusEvent, FormEvent } from 'react';
import { useIntl } from 'react-intl';

import { createClassName, createStyleHelper } from '../../utils/class-names';

import styles from './input.module.scss';

const style = createStyleHelper(styles, 'input');

export interface IInputProps {
    className?: string;

    onChange?: (ev: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (ev: FocusEvent) => void;
    onInput?: (ev: FormEvent<HTMLInputElement>) => void;

    compact?: boolean;
    disabled?: boolean;
    placeholder?: string;
    name?: string;
    value?: string;
    defaultValue?: string;
    autoFocus?: boolean;
    autoComplete?: string;
    maxLen?: number;
    pattern?: string;

    type?: 'text' | 'password' | 'number' | 'email' | 'date';

    testId?: string;
}

export type FullInputProps = ComponentProps<FunctionComponent<IInputProps>>;

export const Input = forwardRef<HTMLInputElement, FullInputProps>(
    (
        {
            className,
            onChange,
            onBlur,
            onInput,
            placeholder,
            name,
            type,
            value,
            defaultValue,
            compact,
            autoFocus,
            autoComplete,
            disabled = false,
            testId,
            maxLen,
            pattern,
        },
        ref,
    ) => {
        const intl = useIntl();

        const translatedPlaceholder =
            name === 'cardHolderName'
                ? 'Cardholder Name'
                : placeholder
                ? intl.formatMessage({
                      id: placeholder,
                      defaultMessage: placeholder,
                  })
                : '';

        const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
            if (name === 'username' && ev.target.value) {
                ev.target.value = ev.target.value.toLowerCase();
            }
            if (onChange) {
                onChange(ev);
            }
        };

        return (
            <input
                ref={ref}
                className={createClassName([className, style(undefined, { compact: !!compact })])}
                placeholder={translatedPlaceholder}
                maxLength={maxLen}
                type={type}
                disabled={disabled}
                name={name}
                pattern={pattern}
                value={value}
                defaultValue={defaultValue}
                onChange={handleChange}
                onBlur={onBlur}
                onInput={onInput}
                autoFocus={autoFocus}
                autoComplete={autoComplete}
                data-testid={testId}
            />
        );
    },
);
